<template>
  <div
    class="offcanvas offcanvas-end"
    id="OffcanvasForm"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ type === 'CREATE'? $t('Create Reservation'): $t('Edit Reservation') }}
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body p-0"
    >
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-circle-info me-2" />
        {{ $t('Reservation Information') }}
      </div>
      <div class="px-3">
        <div class="row g-3 py-2">
          <div class="col-md-12">
            <label
              for="customer_name"
              class="form-label"
            >
              {{ $t('Customer Name') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="customer_name"
                type="text"
                v-model.trim="v$.form.customer_name.$model"
                class="form-control shadow-none"
                :class="v$.form.customer_name.$error? 'is-invalid': ''"
                :placeholder="$t('Enter customer name') + '...'"
              >
              <div v-for="error of v$.form.customer_name.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>

          <div class="col-md-12">
            <label
              for="telephone"
              class="form-label"
            >
              {{ $t('Telephone') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="telephone"
                type="text"
                v-model.trim="v$.form.telephone.$model"
                class="form-control shadow-none"
                :class="v$.form.telephone.$error? 'is-invalid': ''"
                :placeholder="$t('Enter telephone') + '...'"
              >
              <div v-for="error of v$.form.telephone.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <label class="form-label">
              {{ $t('Branch') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group input-group-sm">
              <select
                class="form-select form-select-sm shadow-none"
                v-model="v$.form.branch_id.$model"
                @change="form.table_id = ''"
              >
                <option value="" disabled>{{ $t('Select Branch') + '...' }}</option>
                <option
                  v-for="branch in branches"
                  :key="branch.id"
                  :value="branch.id"
                >
                  {{ $t(branch.name) }}
                </option>
              </select>
            </div>
          </div>

          <div class="col-md-6">
            <label class="form-label">
              {{ $t('Table') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group input-group-sm">
              <select
                class="form-select form-select-sm shadow-none"
                v-model="v$.form.table_id.$model"
                :disabled="!form.branch_id"
              >
                <option value="" disabled>{{ $t('Select Table') + '...' }}</option>
                <option
                  v-for="table in filteredTables"
                  :key="table.id"
                  :value="table.id"
                >
                  {{ $t(table.name) }} ({{ $t(table.zone) }})
                </option>
              </select>
            </div>
          </div>

          <div class="col-md-6">
            <label
              for="guests"
              class="form-label"
            >
              {{ $t('Guests') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="guests"
                type="number"
                min="1"
                v-model.number="v$.form.guests.$model"
                class="form-control shadow-none"
                :class="v$.form.guests.$error? 'is-invalid': ''"
                :placeholder="$t('Enter number of guests') + '...'"
              >
              <div v-for="error of v$.form.guests.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <label
              for="reservation_at"
              class="form-label"
            >
              {{ $t('Reservation Date') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="reservation_at"
                type="datetime-local"
                v-model="v$.form.reservation_at.$model"
                class="form-control shadow-none"
                :class="v$.form.reservation_at.$error? 'is-invalid': ''"
              >
              <div v-for="error of v$.form.reservation_at.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>

          <div class="col-md-12">
            <label class="form-label">
              {{ $t('Status') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group input-group-sm">
              <select
                class="form-select form-select-sm shadow-none"
                v-model="v$.form.state.$model"
              >
                <option
                  v-for="option in stateOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ $t(option.text) }}
                </option>
              </select>
            </div>
          </div>

          <div class="col-md-12">
            <label
              for="note"
              class="form-label"
            >
              {{ $t('Note') }}
            </label>
            <div class="input-group-sm">
              <textarea
                id="note"
                v-model.trim="form.note"
                class="form-control shadow-none"
                rows="3"
                :placeholder="$t('Enter note') + '...'"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
    >
      <ladda
        text="Save"
        v-if="type === 'CREATE'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleCreate"
      />
      <ladda
        text="Save"
        v-if="type === 'EDIT'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleEdit"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation(this.form)
  },
  props: {
    type: {
      type: String,
      default: 'CREATE'
    },
    id: {
      type: [Number, String],
      default: 0
    },
    branches: {
      type: Array,
      default: () => []
    },
    tables: {
      type: Array,
      default: () => []
    },
    stateOptions: {
      type: Array,
      default: () => []
    },
    initialData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loadingSave: false,
      form: {
        customer_name: '',
        telephone: '',
        branch_id: '',
        table_id: '',
        guests: 2,
        reservation_at: '',
        state: 'Pending',
        note: ''
      }
    }
  },
  components: {
    Ladda
  },
  computed: {
    filteredTables() {
      if (!this.form.branch_id) return [];
      return this.tables.filter(t => t.branch_id === this.form.branch_id);
    }
  },
  methods: {
    fillFormFromInitial() {
      if (!this.initialData) return;
      this.form = {
        customer_name: this.initialData.customer_name || '',
        telephone: this.initialData.telephone || '',
        branch_id: this.initialData.branch_id || '',
        table_id: this.initialData.table_id || '',
        guests: this.initialData.guests || 2,
        reservation_at: this.initialData.reservation_at_raw || '',
        state: this.initialData.state || 'Pending',
        note: this.initialData.note || ''
      };
    },
    handleEdit() {
      this.loadingSave = true;
      this.v$.$touch();
      if (this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      this.$parent.handleSave(this.id, { ...this.form }, 'EDIT');
      $(this.$refs.offcanvas).offcanvas('hide');
      this.loadingSave = false;
    },
    handleCreate() {
      this.loadingSave = true;
      this.v$.$touch();
      if (this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      this.$parent.handleSave(null, { ...this.form }, 'CREATE');
      $(this.$refs.offcanvas).offcanvas('hide');
      this.loadingSave = false;
    },
  },
  mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas', () => {
      if (this.type === 'EDIT') this.fillFormFromInitial();
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
  }
}
</script>
